import { FC, ReactNode } from "react";

import { Separator } from "@src/lib/ui/Separator";
import { floatingToolbarSurfaceClassName } from "@src/lib/ui/floatingSurface";
import { cn } from "@src/lib/utils/cn";

/**
 * Floating pill that anchors to the bottom of a relative parent and hosts
 * builder-level controls (undo/redo, formatting, view modes, etc.). Shared
 * across Opus Docs, course builder, and future inline builders so they all
 * present a consistent surface. Context-free: builder-wired controls such as
 * BuilderBottomToolbarUndoRedo live in BuilderBottomToolbar.tsx.
 */
export const BuilderBottomToolbar: FC<{
  children: ReactNode;
  /** The design-system surface (the course builder's canvas tools). */
  designTokens?: boolean;
}> = ({ children, designTokens = false }) => {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 z-[750] flex justify-center",
        // The canvas tools keep every floating control 8px from the edge.
        designTokens ? "bottom-2" : "bottom-3",
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "pointer-events-auto flex items-center gap-1 rounded-full p-1.5",
          // The design-system floating surface: the hairline border shadow
          // under the large shadow draws its edge, so no stroke.
          designTokens
            ? floatingToolbarSurfaceClassName
            : "border border-gray-2 bg-white shadow-lg",
        )}
      >
        {children}
      </div>
    </div>
  );
};

export const BuilderBottomToolbarSeparator: FC = () => (
  <Separator orientation="vertical" className="mx-1 h-6" />
);
